import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Button from '../components/Button';
import AdList from '../components/AdList/AdList';
import { listingService } from '../api/services/ListingService';
import { MediaService } from '../api/services/MediaService';
import { listingViewModelToAdListing, myListingsQueryForSort } from '../api/utils/listingMapper';
import type { ListingViewModel } from '../api/types/listings/ListingViewModel';
import type { AdListing } from '../types/adListing';
import { ViewOption } from '../types/viewOptions';
import { SortingOptions } from '../types/sortingOptions';
import { stripRichTextToPlain } from 'utils/richTextContent';

const Ilanlarim_kurumsal: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const [rawListings, setRawListings] = useState<ListingViewModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [viewType, setViewType] = useState<ViewOption>(ViewOption.GRID);
  const [sortOption, setSortOption] = useState<SortingOptions>(SortingOptions.NEWEST);

  const fetchListings = useCallback(async (userId: string, sort: SortingOptions) => {
    setLoading(true);
    setFetchError(null);
    try {
      const orderQuery = myListingsQueryForSort(sort);
      const result = await listingService.getByUserId(userId, orderQuery);
      if (result.isSuccess && result.data) {
        setRawListings(result.data);
      } else {
        setRawListings([]);
        setFetchError(result.message ?? 'İlanlar yüklenemedi.');
      }
    } catch (err) {
      setRawListings([]);
      setFetchError(err instanceof Error ? err.message : 'Bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/giris', { replace: true });
      return;
    }
    if (user?.id) {
      fetchListings(user.id, sortOption);
    }
  }, [isAuthenticated, user?.id, sortOption, fetchListings, navigate]);

  const sortedListings: AdListing[] = [...rawListings]
    .sort((a, b) => {
      if (sortOption === SortingOptions.PRICE_ASC) return a.price - b.price;
      if (sortOption === SortingOptions.PRICE_DESC) return b.price - a.price;
      return 0;
    })
    .map(listingViewModelToAdListing);

  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSortOption(e.target.value as SortingOptions);
  };

  const handleViewChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setViewType(e.target.value as ViewOption);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getStatusBadge = (statusName: string | null | undefined) => {
    if (!statusName) return null;
    const n = statusName.toLowerCase();
    if (n.includes('satıldı') || n.includes('sold')) return <span className="badge bg-success">Satıldı</span>;
    if (n.includes('iptal') || n.includes('cancel')) return <span className="badge bg-danger">İptal Edildi</span>;
    if (n.includes('süresi dol') || n.includes('expired')) return <span className="badge bg-secondary">Süresi Doldu</span>;
    if (n.includes('aktif') || n === 'active') return <span className="badge bg-primary">Aktif</span>;
    return <span className="badge bg-secondary">{statusName}</span>;
  };

  if (!isAuthenticated) return null;
  if (!user) {
    return (
      <div className="min-vh-100" style={{ backgroundColor: 'var(--sb-bg-muted)' }}>
        <header className="header sticky-top" style={{ backgroundColor: 'var(--sb-bg)' }}>
          <nav className="navbar navbar-expand-lg">
            <div className="container">
              <Link className="navbar-brand fw-bold text-primary" to="/">SadeceBilgisayar.com</Link>
            </div>
          </nav>
        </header>
        <main className="container py-4">
          <div className="text-center py-5 text-muted">Yükleniyor...</div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-vh-100" style={{ backgroundColor: 'var(--sb-bg-muted)' }}>
      <header className="header sticky-top" style={{ backgroundColor: 'var(--sb-bg)' }}>
        <nav className="navbar navbar-expand-lg">
          <div className="container">
            <Link className="navbar-brand fw-bold text-primary" to="/">
              <i className="fas fa-laptop me-2" />
              SadeceBilgisayar.com
            </Link>

            <div className="search-box mx-lg-4 flex-grow-1">
              <form className="d-flex">
                <div className="input-group">
                  <input
                    aria-label="Arama"
                    className="form-control"
                    placeholder="Dizüstü, masaüstü, parça ara..."
                    type="search"
                  />
                  <Button className="btn btn-primary" type="submit">
                    <i className="fas fa-search" />
                  </Button>
                </div>
              </form>
            </div>

            <div className="nav-right d-flex align-items-center">
              <Link className="btn btn-outline-primary me-3" to="/ilan-ver">
                <i className="fas fa-plus-circle me-2" />
                İlan Ver
              </Link>

              <div className="dropdown me-3">
                <Button className="btn btn-link text-dark position-relative" data-bs-toggle="dropdown">
                  <i className="fas fa-bell fs-5" />
                  <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">3</span>
                </Button>
                <div className="dropdown-menu dropdown-menu-end">
                  <h6 className="dropdown-header">Bildirimler</h6>
                  <Link className="dropdown-item" to="#">İlanınıza yeni bir mesaj geldi</Link>
                  <Link className="dropdown-item" to="#">Takip ettiğiniz ürünün fiyatı düştü</Link>
                  <Link className="dropdown-item" to="#">Yeni bir teklif aldınız</Link>
                </div>
              </div>

              <div className="dropdown me-3">
                <Button className="btn btn-link text-dark position-relative" data-bs-toggle="dropdown">
                  <i className="fas fa-envelope fs-5" />
                  <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">2</span>
                </Button>
                <div className="dropdown-menu dropdown-menu-end">
                  <h6 className="dropdown-header">Mesajlar</h6>
                  <Link className="dropdown-item" to="/mesajlar">Tüm mesajları görüntüle</Link>
                </div>
              </div>

              <div className="dropdown">
                <Button className="btn btn-link text-dark dropdown-toggle d-flex align-items-center" data-bs-toggle="dropdown">
                  <span>{user.name || user.email || 'Hesap'}</span>
                </Button>
                <ul className="dropdown-menu dropdown-menu-end">
                  <li>
                    <Link className="dropdown-item" to="/profil-kurumsal">
                      <i className="fas fa-building me-2" />
                      Şirket Profili
                    </Link>
                  </li>
                  <li>
                    <Link className="dropdown-item" to="/ilanlarim-kurumsal">
                      <i className="fas fa-list me-2" />
                      İlanlarım
                    </Link>
                  </li>
                  <li>
                    <Link className="dropdown-item" to="/favoriler-kurumsal">
                      <i className="fas fa-heart me-2" />
                      Favorilerim
                    </Link>
                  </li>
                  <li><hr className="dropdown-divider" /></li>
                  <li>
                    <Button className="dropdown-item" onClick={handleLogout}>
                      <i className="fas fa-sign-out-alt me-2" />
                      Çıkış Yap
                    </Button>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </nav>
      </header>

      <main className="container py-4">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-4">
          <h4 className="mb-0">İlanlarım</h4>
          <div className="d-flex align-items-center gap-2">
            <Button
              className="btn btn-outline-primary"
              onClick={() => setShowHistory(!showHistory)}
            >
              <i className="fas fa-history me-2" />
              İlan Geçmişi
            </Button>
            <Link className="btn btn-primary" to="/ilan-ver">
              <i className="fas fa-plus me-2" />
              Yeni İlan Ver
            </Link>
          </div>
        </div>

        {fetchError && (
          <div className="alert alert-warning mb-3" role="alert">
            {fetchError}
          </div>
        )}

        {showHistory && (
          <div className="card mb-4">
            <div className="card-header" style={{ backgroundColor: 'var(--sb-bg-muted)' }}>
              <h5 className="card-title mb-0">
                <i className="fas fa-history text-primary me-2" />
                İlan Geçmişi
              </h5>
            </div>
            <div className="list-group list-group-flush">
              {rawListings.map((listing) => (
                <div key={listing.id} className="list-group-item">
                  <div className="d-flex align-items-center">
                    <img
                      alt={listing.title ?? ''}
                      className="rounded me-3"
                      src={MediaService.toImageSrc(listing.imageUrls?.[0] ?? listing.images?.[0]?.url ?? listing.images?.[0]?.id ?? '')}
                      width="100"
                      height="80"
                      style={{ objectFit: 'cover' }}
                    />
                    <div className="flex-grow-1">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="mb-0">{listing.title}</h6>
                        {getStatusBadge(listing.status?.name ?? null)}
                      </div>
                      <p className="text-muted small mb-2">
                        {(() => {
                          const plain = stripRichTextToPlain(listing.description);
                          return plain.length > 120 ? `${plain.slice(0, 120)}...` : plain;
                        })()}
                      </p>
                      {listing.statusId === 5 && listing.rejectionReason && (
                        <div className="alert alert-danger small py-2 px-2 mb-2" role="status">
                          <strong>Red gerekçesi:</strong> {listing.rejectionReason}
                        </div>
                      )}
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="text-muted small">
                          <i className="fas fa-eye me-1" />
                          {listing.viewCount ?? 0} görüntülenme
                        </div>
                        <div className="fw-bold text-primary">{listing.price.toLocaleString('tr-TR')} ₺</div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {rawListings.length === 0 && !loading && (
                <div className="list-group-item text-center text-muted py-4">Henüz ilan yok.</div>
              )}
            </div>
          </div>
        )}

        {!showHistory && (
          <>
            {loading ? (
              <div className="text-center py-5 text-muted">Yükleniyor...</div>
            ) : sortedListings.length === 0 ? (
              <div className="text-center py-5">
                <i className="fas fa-box-open fa-3x text-muted mb-3" />
                <h3>Henüz ilanınız yok</h3>
                <p className="text-muted">Yeni bir ilan oluşturarak satışa başlayabilirsiniz.</p>
                <Link className="btn btn-primary" to="/ilan-ver">İlan Ver</Link>
              </div>
            ) : (
              <AdList
                sortedIlanlar={sortedListings}
                itemsPerPage={sortedListings.length}
                currentPage={1}
                totalPages={1}
                sortOption={sortOption}
                viewType={viewType}
                handleSortChange={handleSortChange}
                handleViewChange={handleViewChange}
                detailLinkBase="/ilan-detay"
              />
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default Ilanlarim_kurumsal;
